/**
 * Minimal cURL → http_request config parser (ADR-0009 Track 3).
 *
 * n8n's "Import cURL" convenience: paste a `curl ...` command and
 * pre-fill the node's method / url / headers / body. Deliberately
 * bounded — it covers the flags people actually paste from browser
 * devtools / API docs, not the full curl surface:
 *
 *   -X / --request <m>            method
 *   <url> / --url <url>           target URL (first bare token or --url)
 *   -H / --header "K: V"          header (repeatable)
 *   -d / --data / --data-raw /    request body → json if it parses as
 *     --data-binary / --data-ascii   JSON (and no conflicting CT), else raw
 *   -u / --user user:pass         → Authorization: Basic base64 header
 *   --compressed, -s, -k, -L, …   ignored (no config effect here)
 *
 * Returns a partial config patch to merge into the node. Throws
 * `Error("curl_parse_failed:…")` on input that isn't a curl command.
 */

export interface CurlConfigPatch {
  method?: string;
  url?: string;
  headers?: Record<string, string>;
  body_content_type?: "none" | "json" | "raw";
  body_json?: string;
  body_raw?: string;
}

/** Split a shell-ish command into tokens, honouring single/double
 *  quotes and backslash line-continuations. Not a full shell lexer —
 *  enough for pasted curl commands. */
export function tokenizeShell(input: string): string[] {
  // Drop line-continuation backslashes (curl commands are often
  // multi-line with trailing ` \`).
  const src = input.replace(/\\\r?\n/g, " ");
  const tokens: string[] = [];
  let cur = "";
  let quote: '"' | "'" | null = null;
  let has = false; // current token has content (even if empty string "")
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quote) {
      if (c === quote) {
        quote = null;
      } else if (c === "\\" && quote === '"' && i + 1 < src.length) {
        // In double quotes, backslash escapes the next char.
        cur += src[++i];
      } else {
        cur += c;
      }
      continue;
    }
    if (c === '"' || c === "'") {
      quote = c;
      has = true;
      continue;
    }
    if (c === "\\" && i + 1 < src.length) {
      cur += src[++i];
      has = true;
      continue;
    }
    if (/\s/.test(c)) {
      if (has) {
        tokens.push(cur);
        cur = "";
        has = false;
      }
      continue;
    }
    cur += c;
    has = true;
  }
  if (has) tokens.push(cur);
  return tokens;
}

function base64(s: string): string {
  // btoa exists in the browser + jsdom; guard for safety.
  if (typeof btoa === "function") return btoa(s);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const B = (globalThis as any).Buffer;
  return B ? B.from(s, "utf-8").toString("base64") : s;
}

export function parseCurl(command: string): CurlConfigPatch {
  const tokens = tokenizeShell(command.trim());
  if (tokens.length === 0 || tokens[0] !== "curl") {
    throw new Error("curl_parse_failed:not a curl command (must start with `curl`)");
  }

  const headers: Record<string, string> = {};
  let method: string | undefined;
  let url: string | undefined;
  const dataParts: string[] = [];

  const dataFlags = new Set(["-d", "--data", "--data-raw", "--data-binary", "--data-ascii"]);

  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === "-X" || t === "--request") {
      method = tokens[++i]?.toUpperCase();
    } else if (t === "-H" || t === "--header") {
      const raw = tokens[++i] ?? "";
      const idx = raw.indexOf(":");
      if (idx > 0) {
        headers[raw.slice(0, idx).trim()] = raw.slice(idx + 1).trim();
      }
    } else if (t === "--url") {
      url = tokens[++i];
    } else if (dataFlags.has(t)) {
      dataParts.push(tokens[++i] ?? "");
    } else if (t === "-u" || t === "--user") {
      const creds = tokens[++i] ?? "";
      headers["Authorization"] = `Basic ${base64(creds)}`;
    } else if (t.startsWith("-")) {
      // Unknown flag. Skip; consume a value only when the flag clearly
      // takes one (best-effort: flags we don't model are mostly booleans).
      continue;
    } else if (!url) {
      // First bare token is the URL.
      url = t;
    }
  }

  const patch: CurlConfigPatch = {};
  if (url) patch.url = url;
  // Method defaults to POST when a body is present and -X wasn't given
  // (mirrors curl's behaviour), else GET.
  patch.method = method ?? (dataParts.length > 0 ? "POST" : "GET");
  if (Object.keys(headers).length > 0) patch.headers = headers;

  if (dataParts.length > 0) {
    const body = dataParts.join("&");
    const ctHeader = Object.entries(headers).find(([k]) => k.toLowerCase() === "content-type")?.[1];
    const looksJson = (!ctHeader || ctHeader.toLowerCase().includes("json")) && isJson(body);
    if (looksJson) {
      patch.body_content_type = "json";
      patch.body_json = body;
    } else {
      patch.body_content_type = "raw";
      patch.body_raw = body;
    }
  }

  return patch;
}

function isJson(s: string): boolean {
  try {
    const v = JSON.parse(s);
    return typeof v === "object" && v !== null;
  } catch {
    return false;
  }
}
